import React from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '~/components';
import { colors } from '~/styles';
import Chart from './Chart';
import Markdown from './Markdown';
import { resolveRenderer } from './renderers';
import ResponseActions from './ResponseActions';
import ToolStatusBar from './ToolStatusBar';

const S = {
  Turn: styled.div`
    position: relative;
    display: flex;
    align-items: flex-start;
    margin-top: 1rem;

    ${(props) =>
      props.$last &&
      css`
        min-height: 70vh;
      `}
  `,

  Message: styled.div`
    align-self: flex-start;
    width: 100%;
    position: relative;

    &:hover .response-actions {
      opacity: 1;
    }
    color: ${colors.black};
    overflow: hidden;
    line-height: 1.5;

    h1,
    h2,
    h3 {
      font-weight: 600;
      color: ${colors.black};
      margin: 1rem 0 0.5rem;
    }

    h1 {
      font-size: 1.125rem;
    }

    h2 {
      font-size: 1rem;
    }

    h3 {
      font-size: 0.875rem;
    }

    p {
      margin-bottom: 0.5rem;
    }

    blockquote {
      border-left: 0.1875rem solid ${colors.grey25};
      padding-left: 0.75rem;
      margin: 0.5rem 0;
      color: ${colors.grey75};
    }

    ol {
      margin-bottom: 0.5rem;
      padding-left: 1rem;
      list-style-position: inside;
    }

    ul {
      margin-bottom: 0.5rem;
      list-style-position: inside;
      list-style-type: disc;
      padding-left: 1rem;

      ul {
        list-style-type: circle;

        ul {
          list-style-type: square;
        }
      }
    }

    li > p {
      display: inline;
      margin-bottom: 0;
    }
  `,

  ErrorBlock: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: ${({ $variant }) => ($variant === 'warning' ? colors.warning10 : colors.danger5)};
    border: 1px solid ${({ $variant }) => ($variant === 'warning' ? colors.warning25 : colors.danger25)};
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    color: ${({ $variant }) => ($variant === 'warning' ? colors.warning100 : colors.danger)};
    font-size: 0.875rem;
    line-height: 1.5;
  `,

  ErrorIcon: styled.span`
    flex-shrink: 0;
  `,

  ThinkingSparkles: styled.div`
    font-size: 0.75rem;
    margin-top: 0.5rem;

    svg {
      animation: fa-pulse-opacity 1.4s ease-in-out infinite;
    }

    @keyframes fa-pulse-opacity {
      0% {
        opacity: 0.35;
      }
      50% {
        opacity: 0.9;
      }
      100% {
        opacity: 0.35;
      }
    }
  `,
};

function Parts({ parts, isLast }) {
  // Map callId → downloadable recordset (from visible data parts and from
  // hidden raw-recordset parts) so the status bar can offer a per-execution CSV.
  const downloads = {};
  for (const p of parts) {
    if (p.callId && p.data && p.fields) {
      downloads[p.callId] = { data: p.data, fields: p.fields, filename: p.filename };
    }
  }

  return parts.map((part, i) => {
    const key = `${part.type}-${i}`;
    // Hidden parts are storage-only (raw recordsets for status-bar download).
    if (part.hidden) return null;
    switch (part.type) {
      case 'tools':
        return <ToolStatusBar key={key} tools={part.tools} downloads={downloads} />;

      case 'text':
        return <Markdown key={key} content={part.content} />;

      case 'chart':
        return (
          <Chart
            key={key}
            chartType={part.chartType}
            data={part.data}
            fields={part.fields}
            dimension={part.dimension}
            metric={part.metric}
            series={part.series}
            colors={part.colors}
            filename={part.filename}
          />
        );

      case 'table':
      case 'schedule':
      case 'pivot':
      case 'component': {
        const tool = part.meta?.contract?.tool;
        const Renderer = resolveRenderer(tool, part.type);
        // 'component' has no generic default — an unregistered tool renders nothing rather than crashing.
        if (!Renderer) return null;
        return (
          <Renderer
            key={key}
            data={part.data}
            fields={part.fields}
            display={part.display}
            meta={part.meta}
            pagination={part.pagination}
            filename={part.filename}
            cellValue={part.cellValue}
            isLast={isLast}
          />
        );
      }

      case 'error': {
        const variant = part.code === 'AI_BUDGET_LIMIT' ? 'warning' : 'danger';
        const iconColor = variant === 'warning' ? colors.warning100 : colors.danger;
        return (
          <S.ErrorBlock key={key} $variant={variant}>
            <S.ErrorIcon>
              <Icon icon="exclamation-triangle" color={iconColor} />
            </S.ErrorIcon>
            {part.message}
          </S.ErrorBlock>
        );
      }

      default:
        return null;
    }
  });
}

export default function AssistantMessage({ message, conversationId, isLast, thinking }) {
  const hasContent = message.parts.some(
    (p) => !p.hidden && (p.type === 'text' || p.type === 'table' || p.type === 'chart' || p.type === 'component'),
  );

  return (
    <S.Turn $last={isLast}>
      <S.Message>
        <Parts parts={message.parts} isLast={isLast} />

        {thinking && (
          <S.ThinkingSparkles>
            <Icon icon="sparkles" color={colors.primary} />
          </S.ThinkingSparkles>
        )}

        {!thinking && hasContent && <ResponseActions message={message} conversationId={conversationId} />}
      </S.Message>
    </S.Turn>
  );
}
